<template>
  <div class="index-input-wrapper" :class="wrapperClasses" v-on="filteredListeners">
    <!-- 前置插槽 -->
    <div v-if="$slots.prepend" class="index-input-slot-prepend">
      <slot name="prepend"></slot>
    </div>
    <!-- 前缀文本插槽 -->
    <div v-if="$slots.prefix || prefixText" class="index-input-prefix">
      <slot name="prefix">{{ prefixText }}</slot>
    </div>
    <!-- 输入框 -->
    <el-input-digit
      :value="internalValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :precision="precision"
      :format="format"
      :nullable="nullable"
      :style="inputWidthStyle"
      :readonly="readonly"
      class="index-input-core"
      @input="handleInput"
      @change="handleChange"
    />
    <!-- 后缀文本插槽 -->
    <div v-if="$slots.suffix || suffixText" class="index-input-suffix">
      <slot name="suffix">{{ suffixText }}</slot>
    </div>
    <!-- 后置插槽 -->
    <div v-if="$slots.append" class="index-input-slot-append">
      <slot name="append"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'IndexInput',
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    // 数值输入相关
    value: {
      type: [Number, String],
      default: ''
    },
    placeholder: {
      type: String,
      default: '请输入'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly:{
      type:Boolean,
      default:false
    },
    precision: {
      type: Number,
      default: 2
    },
    format: {
      type: String/Boolean,
      default: '#.##'
    },
    nullable: {
      type: Boolean,
      default: true
    },
    width: {
      type: String,
      default: '80px'
    },
    // 前缀文本（不使用 slot 时显示）
    prefixText: {
      type: String,
      default: ''
    },
    // 后缀文本（不使用 slot 时显示）
    suffixText: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      internalValue: this.value
    };
  },
  computed: {
    wrapperClasses() {
      const classes = [];
      if (this.$slots.prepend || this.prefixText) {
        classes.push('has-prepend');
      }
      if (this.$slots.append || this.$slots.suffix || this.suffixText) {
        classes.push('has-append');
      }
      return classes;
    },
    inputWidthStyle() {
      return { width: this.width };
    },
    filteredListeners() {
      // 过滤掉 input 和 change 事件，避免重复触发
      const listeners = { ...this.$listeners };
      delete listeners.input;
      delete listeners.change;
      return listeners;
    }
  },
  watch: {
    value(v) {
      // 如果父组件传回的是 Event 对象，忽略它
      if (v instanceof Event) return;
      this.internalValue = v;
    },
  },
  methods: {
    handleInput(val) {
      // input 事件可能传值或 Event 对象
      const actualVal = val instanceof Event || (val && val.target) ? val.target.value : val;
      this.internalValue = actualVal;
      this.$emit('input', actualVal);
      this.$emit('change', actualVal);
    },
    handleChange(val) {
      // change 事件可能传值或 Event 对象
      const actualVal = val instanceof Event || (val && val.target) ? val.target.value : val;
      this.$emit('change', actualVal);
    },
    handleFocus(e) {
      // this.$emit('focus', e);
    },
    handleBlur(e) {
      // this.$emit('blur', e);
    }
  }
};
</script>

<style lang="scss" scoped>
.index-input-wrapper {
  display: flex;
  align-items: stretch;

  // 有前置内容时，输入框左边框不圆角
  &.has-prepend {
    ::v-deep .index-input-core .el-input__inner {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }

  // 有后置内容时，输入框右边框不圆角
  &.has-append {
    ::v-deep .index-input-core .el-input__inner {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  // 前置插槽容器 - 支持下拉框
  ::v-deep .index-input-slot-prepend {
    display: flex;
    align-items: stretch;
    flex-shrink: 0;
    padding: 0 5px;
    background-color: #f5f7fa;
    border: 1px solid #dcdfe6;
    border-right: none;
    border-radius: 4px 0 0 4px;

    .el-select {
      height: 100%;
      .el-input {
        height: 100%;
      }
      .el-input__inner {
        padding: 0;
        border: none;
        background: transparent;
        height: 100%;
        line-height: normal;
      }
    }
  }

  ::v-deep .index-input-prefix {
    display: flex;
    align-items: center;
    padding: 0 10px;
    background-color: #f5f7fa;
    border: 1px solid #dcdfe6;
    border-right: none;
    border-radius: 4px 0 0 4px;
    flex-shrink: 0;
  }

  // 后置插槽容器 - 支持下拉框
  ::v-deep .index-input-slot-append {
    display: flex;
    align-items: stretch;
    flex-shrink: 0;
    padding: 0 5px;
    background-color: #f5f7fa;
    border: 1px solid #dcdfe6;
    border-left: none;
    border-radius: 0 4px 4px 0;
    margin-left: -1px;
    position: relative;
    z-index: 0;

    .el-select {
      height: 100%;
      .el-input {
        height: 100%;
      }
      .el-input__inner {
        padding: 0;
        border: none;
        background: transparent;
        height: 100%;
        line-height: normal;
      }
    }
  }

  ::v-deep .index-input-suffix {
    display: flex;
    align-items: center;
    padding: 0 10px;
    background-color: #f5f7fa;
    border: 1px solid #dcdfe6;
    border-left: none;
    border-radius: 0 4px 4px 0;
    margin-left: -1px;
    position: relative;
    z-index: 0;
    flex-shrink: 0;
  }

  // 输入框基础样式
  ::v-deep .index-input-core {
    flex-shrink: 0;
    position: relative;
    z-index: 1;

    .el-input {
      height: 100%;
    }
    .el-input__inner {
      height: 100%;
      line-height: normal;
    }
  }

  // 前置内容与输入框连接处理
  ::v-deep .index-input-slot-prepend + .index-input-core,
  ::v-deep .index-input-prefix + .index-input-core {
    margin-left: -1px;
  }
}
</style>
